/**
 * useDealActivitiesRealtime - Realtime do Negócio aberto (timeline + linha do deal)
 *
 * Subscreve eventos Postgres Changes no MESMO canal, filtrados pelo deal aberto:
 * - `crm_deal_activities` (por `deal_id`): invalida as queries da timeline
 *   `DealActivityTimelinePremium` (aba "Atividades" e bloco "Atividades Recentes").
 * - `crm_deals` (por `id`): invalida `['crm-deal', dealId]` e contadores, garantindo
 *   que trilha de fases (DealPhaseTrail), header (status/valor) e sidebar reflitam
 *   em tempo real alterações feitas por outro usuário/aba, sem F5.
 *
 * Por que um hook dedicado e não estender `useSchedulingRealtime`:
 * - Filtro pelo deal aberto (canal mais barato — só recebe eventos daquele deal).
 * - Vive apenas enquanto o modal do deal está aberto; sem processamento ocioso.
 * - Sem acoplamento com o módulo de Agendamento.
 *
 * CHANGELOG:
 * @version 1.2.0
 * RELEASE: 2026-09-03 v1.2.0 - Adicionado listener em `crm_deals` (filtro `id`) no
 *          mesmo canal: invalida `crm-deal`/`deal-modal-data`/`kanban-column`/
 *          `pipeline-phase-stats` quando a linha do negócio muda fora da sessão
 *          (trilha de fases não atualizava sem F5).
 *          refletir na hora eventos vindos de fora da tela (ex.: aceite da proposta pelo link
 *          público), sem depender de fechar e reabrir o modal.
 * RELEASE: 2026-05-28 v1.0.1 - Nome de canal único por montagem (crypto.randomUUID) para
 *          evitar erro "cannot add postgres_changes callbacks after subscribe()" ao reabrir
 *          o modal do mesmo deal em sequência (Negócio → Cliente → Negócios → reabrir).
 * RELEASE: 2026-04-19 v1.0.0 - Criação inicial
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger dedicado para este hook
const logger = createLogger('DealActivitiesRealtime');

/**
 * Subscreve Realtime em `crm_deal_activities` para o deal informado e invalida
 * os caches da timeline ao receber qualquer evento (INSERT/UPDATE/DELETE).
 *
 * @param dealId - ID do deal aberto (null/undefined = não subscreve)
 */
export function useDealActivitiesRealtime(dealId: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Sem deal aberto: não cria canal
    if (!dealId) return;

    logger.debug('Iniciando Realtime para atividades do deal', { dealId });

    // Nome único por montagem para evitar colisão com canal cacheado pelo supabase-js
    // (ocorria ao reabrir o modal do mesmo deal rapidamente)
    const channelName = `deal-activities-rt-${dealId}-${crypto.randomUUID()}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'crm_deal_activities',
          filter: `deal_id=eq.${dealId}`,
        },
        (payload) => {
          logger.debug('Realtime: crm_deal_activities alterado', {
            eventType: payload.eventType,
            dealId,
          });
          // Invalida as duas chaves consumidas pela timeline (paginada e não-paginada)
          queryClient.invalidateQueries({ queryKey: ['crm-deal-activities-paginated', dealId] });
          queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
          // v1.1.0: a esteira (Workflow) também muda a partir dos mesmos eventos
          // (aceite pelo link público, contrato assinado, cobrança gerada, worker...).
          // Como este hook vive enquanto o modal estiver aberto, ele cobre todas as abas.
        }
      )
      // v1.2.0: listener no MESMO canal para a linha do próprio negócio.
      // Sem ele, mudanças feitas em outro computador/aba (fase, status, valor)
      // não refletiam na trilha de fases/header do modal aberto até um F5.
      .on(
        'postgres_changes',
        {
          event: '*', // UPDATE/DELETE (INSERT irrelevante: modal só abre para deal existente)
          schema: 'public',
          table: 'crm_deals',
          filter: `id=eq.${dealId}`,
        },
        (payload) => {
          logger.debug('Realtime: crm_deals alterado', {
            eventType: payload.eventType,
            dealId,
          });
          // Refetch da linha do negócio → trilha de fases, header e sidebar
          queryClient.invalidateQueries({ queryKey: ['crm-deal', dealId] });
          // Contadores das abas (itens/contratos/tarefas etc. afetados em cascata)
          queryClient.invalidateQueries({ queryKey: ['deal-modal-data', dealId] });
          // Mantém o Kanban de fundo coerente (coluna de origem/destino e stats)
          queryClient.invalidateQueries({ queryKey: ['kanban-column'] });
          queryClient.invalidateQueries({ queryKey: ['pipeline-phase-stats'] });
        }
      )
      .subscribe((status) => {
        logger.debug('Realtime deal activities subscription status:', status);
      });

    // Cleanup ao fechar modal ou trocar de deal
    return () => {
      logger.debug('Removendo Realtime de atividades do deal', { dealId });
      supabase.removeChannel(channel);
    };
  }, [dealId, queryClient]);
}
